import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import React, { useEffect, useState, useCallback } from 'react'
import { useNotify, useRefresh } from 'react-admin'
import { getAvailableDevices, getStreamFromDevice, handlePermissions } from '../../utils/media'
import { recordTestResult, getLabTestAdviceCodeFromResult } from '../../utils/progressKit'
import { formatDateToBrowserLocale } from '../../utils/helpers'
import { fetchResultImages } from '../details/helpers/kitDetailsHelpers'
import { client } from '../../providers/data/client'
import ImagePreview from '../common/ImagePreview'
import moment from 'moment'
import watermark from 'watermarkjs'
import { labTestAdviceCodeDetail } from '../../utils/constants'

const modifyCollectionDateForEdit = (collectionDate) => {
  const [month, day, year] = collectionDate.split('/')
  return `${year}-${month}-${day}`
}

const TestRecordModal = ({ modalState, open, handleClose, edit = false }) => {
  const notify = useNotify()
  const refresh = useRefresh()
  const navigate = useNavigate()
  const [resultRecord, setResultRecord] = useState({
    collectionDate: edit && modalState.specimenCollectionDate ? modifyCollectionDateForEdit(modalState.specimenCollectionDate) : '',
    result: edit && modalState.labTestResult ? modalState.labTestResult : '',
    sampleGrade: edit && modalState.labTestSampleGrade ? modalState.labTestSampleGrade : '',
    note: edit && modalState.notes ? modalState.notes : '',
    providerNotes: edit && modalState.labTestProviderNotes ? modalState.labTestProviderNotes : '',
    adviceCode: edit && modalState.labTestAdviceCode ? modalState.labTestAdviceCode : '',
  })

  const [adviceCodeDisabled, setAdviceCodeDisabled] = useState(true)

  const [errorState, setErrorState] = useState({
    collectionDate: null,
    result: null,
    sampleGrade: null,
    note: null,
    providerNotes: null,
    adviceCode: null,
  })

  const [camerasList, setCamerasList] = useState([])
  const [selectedCamera, setSelectedCamera] = useState('')
  const [capturedImages, setCapturedImages] = useState([])
  const [existingImages, setExistingImages] = useState([])
  const [patientIdCard, setPatientIdCard] = useState([])
  const [imagePreviewImagePath, setImagePreviewImagePath] = useState('')
  const [imagePreviewState, setImagePreviewState] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [showImageWarning, setShowImageWarning] = useState(false)
  const [cameraPermissions, setCameraPermissions] = useState({ video: false, audio: false })

  const handleImagePreview = (bool) => {
    setImagePreviewState(bool)
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  // Check if there are any validation errors on Tab 1
  const hasTab1Errors = () => {
    return Object.values(errorState).some((error) => error !== null)
  }

  const clearCamera = () => {
    const videoElement = document.getElementById('camera-video')
    setSelectedCamera('')
    if (videoElement && videoElement.srcObject) {
      const tracks = videoElement.srcObject.getTracks()
      tracks.forEach((track) => track.stop())
      videoElement.srcObject = null
    }
  }

  const clearStates = useCallback(() => {
    setCapturedImages([])
    setResultRecord({
      collectionDate: '',
      result: '',
      sampleGrade: '',
      note: '',
      providerNotes: '',
      adviceCode: '',
    })
    setCamerasList([])
    setCameraPermissions({ video: false, audio: false })
    clearCamera()
  }, [])

  useEffect(() => {
    const { collectionDate, result, sampleGrade } = resultRecord
    const patientDoV = modalState?.patientDoV

    const updateAdviceCode = () => {
      if (result && sampleGrade) {
        setAdviceCodeDisabled(false)

        // Use 0.0 weeks if no collection date is provided
        const weeksSinceVasectomy = collectionDate ? calculateWeeksSinceVasectomy(patientDoV, collectionDate) : '0.0'

        const newAdviceCode = getLabTestAdviceCodeFromResult({
          weeksSinceVasectomy,
          labTestResult: result,
          labTestSampleGrade: sampleGrade,
        })

        if (newAdviceCode) {
          setResultRecord((prev) => {
            if (prev.adviceCode !== newAdviceCode) {
              return { ...prev, adviceCode: newAdviceCode }
            }
            return prev
          })

          // Clear advice code error since it's automatically calculated
          if (errorState.adviceCode) {
            setErrorState((prev) => ({
              ...prev,
              adviceCode: null,
            }))
          }
        }
      } else {
        setAdviceCodeDisabled(true)
        setResultRecord((prev) => {
          if (prev.adviceCode !== '') {
            return { ...prev, adviceCode: '' }
          }
          return prev
        })
      }
    }

    updateAdviceCode()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultRecord.collectionDate, resultRecord.result, resultRecord.sampleGrade, modalState?.patientDoV, errorState.adviceCode])

  useEffect(() => {
    if (edit) {
      ; (async () => {
        const images = await fetchResultImages(modalState.kitId)
        setExistingImages(images)
      })()
    }
  }, [edit, modalState])

  useEffect(() => {
    existingImages.forEach((image) => {
      if (image.isPatientIdCard) setPatientIdCard((prev) => [...prev, image.url])
    })
  }, [existingImages])

  useEffect(() => {
    if (open) {
      ; (async () => {
        const permissions = await handlePermissions()
        setCameraPermissions(permissions)

        if (permissions.video) {
          const lastDeviceId = localStorage.getItem('selectedDeviceId')
          if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
            const devices = await getAvailableDevices()
            setCamerasList(devices)
            // Set to last deviceId, if any
            if (lastDeviceId) {
              setSelectedCamera(lastDeviceId)
            } else if (devices.length > 0) {
              setSelectedCamera(devices[0].deviceId)
            }
          } else {
            notify("Browser doesn't support cameras!", { type: 'error' })
          }
        } else {
          notify('Camera permission is required for image capture. Please grant permission and reload the page.', { type: 'error' })
        }
      })()
    }

    return () => {
      clearCamera()
      if (open) {
        clearStates()
      }
    }
  }, [open, notify, clearStates])

  const handleCameraSelect = useCallback(
    async (deviceId) => {
      try {
        localStorage.setItem('selectedDeviceId', deviceId)
        const videoElement = document.getElementById('camera-video')
        setSelectedCamera(deviceId)

        if (!videoElement) {
          console.warn('Video element not found, camera may not be initialized yet')
          return
        }

        // Clear any existing stream
        if (videoElement.srcObject) {
          const tracks = videoElement.srcObject.getTracks()
          tracks.forEach((track) => track.stop())
          videoElement.srcObject = null // Ensure previous stream is fully released
        }

        const stream = await getStreamFromDevice(deviceId)
        if (videoElement && stream) {
          videoElement.srcObject = stream
          await videoElement.play()
        } else {
          // Only show error if stream is not available
          notify('Error accessing camera. Please check permissions and try again.', { type: 'error' })
        }
      } catch (error) {
        // Only log error, do not notify user for transient errors
        console.error('Error accessing camera:', error)
      }
    },
    [notify]
  )

  // Initialize camera when switching to Tab 2
  useEffect(() => {
    if (activeTab === 1 && selectedCamera && open) {
      // Small delay to ensure video element is rendered
      setTimeout(() => {
        handleCameraSelect(selectedCamera)
      }, 100)
    }
  }, [activeTab, selectedCamera, open, handleCameraSelect])

  // Cleanup camera on component unmount and page navigation
  useEffect(() => {
    const handleBeforeUnload = () => {
      clearCamera()
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearCamera()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearCamera()
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  const valid = () => {
    let result = true
    const errorObj = {
      collectionDate: null,
      result: null,
      sampleGrade: null,
      note: null,
      providerNotes: null,
      adviceCode: null,
    }

    /*
    if (resultRecord.collectionDate === '') {
      errorObj.collectionDate = 'Collection Date is required'
      result = false
    }
    */

    if (resultRecord.result === 'error' && resultRecord.note === '') {
      errorObj.note = "Note is required when test result is 'error'"
      result = false
    }

    if (resultRecord.result === '') {
      errorObj.result = 'Result is required'
      result = false
    }
    if (resultRecord.sampleGrade === '') {
      errorObj.sampleGrade = 'Sample Grade is required'
      result = false
    }
    if (resultRecord.adviceCode === '') {
      errorObj.adviceCode = 'Advice Code is required'
      result = false
    }
    setErrorState(errorObj)
    return result
  }

  const handleSave = async (forceSubmit = false) => {
    setIsSaving(true)
    try {
      if (!valid()) {
        setIsSaving(false)
        return
      }

      const totalImages = existingImages.length + capturedImages.length

      // Check if user has less than 4 images and hasn't confirmed yet
      if (totalImages < 4 && !forceSubmit) {
        setIsSaving(false)
        setShowImageWarning(true)
        return
      }

      clearCamera()
      await recordTestResult({ ...resultRecord, images: capturedImages }, patientIdCard, modalState, notify, refresh, navigate)
      setIsSaving(false)
      handleClose()
    } catch (error) {
      setIsSaving(false)
      console.log(error)

      // Provide more specific error messages based on the error type
      let errorMessage = 'Failed to save test record. Please try again.'

      if (error.response) {
        // First check if we have a specific error message from the backend
        if (error.response.data?.message) {
          // Direct error message from backend
          errorMessage = error.response.data.message
        }
        // If no specific error message, fallback to status-based messages
        else if (error.response.status === 413) {
          errorMessage = 'Images are too large. Please reduce image size or number of images and try again.'
        } else if (error.response.status === 400) {
          errorMessage = 'Invalid data provided. Please check all fields and try again.'
        } else if (error.response.status === 500) {
          errorMessage = 'Server error occurred. Please try again in a few moments.'
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error. Please check your connection and try again.'
      } else if (error.message?.includes('Failed to fetch')) {
        errorMessage = 'Network connection failed. Please check your internet connection.'
      }

      notify(errorMessage, { type: 'error' })
    }
  }

  const handleConfirmSave = async () => {
    setShowImageWarning(false)
    await handleSave(true)
  }

  const handleDelete = async (image) => {
    if (patientIdCard.includes(image)) {
      setPatientIdCard(patientIdCard.filter((i) => i !== image))
    }

    if (image.includes('http') && edit) {
      const tempList = [...existingImages]
      const deleteableImage = tempList.splice(
        existingImages.findIndex((i) => i.url === image),
        1
      )
      const splitedKey = deleteableImage[0].key.split('/')
      const response = await client().delete(`kits/${modalState?.kitId}/artifact/${splitedKey[splitedKey.length - 1]}`)
      if (response.data.success) setExistingImages(tempList)
      return
    }

    const tempList = [...capturedImages]
    tempList.splice(capturedImages.indexOf(image), 1)
    setCapturedImages(tempList)
    return
  }

  const handleInput = (target) => {
    const { name, value } = target

    // Clear the error for this field when user provides input
    if (errorState[name] && value !== '') {
      setErrorState((prev) => ({
        ...prev,
        [name]: null,
      }))
    }

    if (name === 'result') {
      let clearSampleGradeError = false
      // Clear result error when a valid result is selected
      if (errorState.result && value !== '') {
        setErrorState((prev) => ({
          ...prev,
          result: null,
        }))
      }

      // Clear note error when changing from 'error' to other values
      if (resultRecord.result === 'error' && value !== 'error' && errorState.note) {
        setErrorState((prev) => ({
          ...prev,
          note: null,
        }))
      }

      switch (value) {
        case 'NSS':
          setResultRecord({
            ...resultRecord,
            [name]: value,
            sampleGrade: 'NONE',
          })
          clearSampleGradeError = true
          break
        case 'Positive':
          setResultRecord({
            ...resultRecord,
            [name]: value,
            sampleGrade: 'HIGH',
          })
          clearSampleGradeError = true
          break
        case 'error':
          setResultRecord({
            ...resultRecord,
            [name]: value,
            sampleGrade: 'NONE',
          })
          clearSampleGradeError = true
          break
        default:
          break
      }

      if (clearSampleGradeError) {
        setErrorState((prev) => ({
          ...prev,
          sampleGrade: null,
        }))
      }
      return
    }

    // Special handling for note field when result is 'error'
    if (name === 'note' && resultRecord.result === 'error' && value !== '') {
      setErrorState((prev) => ({
        ...prev,
        note: null,
      }))
    }

    setResultRecord({
      ...resultRecord,
      [name]: value,
    })
  }

  const calculateWeeksSinceVasectomy = (vasectomyDate, collectionDate) => {
    // Return 0 if either date is missing
    if (!vasectomyDate || !collectionDate) {
      return '0.0'
    }

    let vasectomyMoment, collectionMoment

    // Handle vasectomy date - could be unix timestamp or date string
    if (typeof vasectomyDate === 'number' || (typeof vasectomyDate === 'string' && !isNaN(vasectomyDate))) {
      // Unix timestamp
      vasectomyMoment = moment.unix(Number(vasectomyDate))
    } else {
      // Date string - try common formats
      const formats = ['MM/DD/YYYY', 'MM-DD-YYYY', 'YYYY-MM-DD', 'MM.DD.YY', 'M.DD.YY', 'MM/DD/YY', 'YYYYMMDD']
      vasectomyMoment = moment(vasectomyDate, formats, true)
    }

    // Handle collection date - likely in YYYY-MM-DD format from date input
    if (typeof collectionDate === 'number' || (typeof collectionDate === 'string' && !isNaN(collectionDate))) {
      // Unix timestamp
      collectionMoment = moment.unix(Number(collectionDate))
    } else {
      // Date string - try common formats, prioritizing YYYY-MM-DD for HTML date inputs
      const formats = ['YYYY-MM-DD', 'MM/DD/YYYY', 'MM-DD-YYYY', 'MM.DD.YY', 'M.DD.YY', 'MM/DD/YY', 'YYYYMMDD']
      collectionMoment = moment(collectionDate, formats, true)
    }

    // Validate that both dates are valid
    if (!vasectomyMoment.isValid() || !collectionMoment.isValid()) {
      console.warn('Invalid dates provided to calculateWeeksSinceVasectomy:', {
        vasectomyDate,
        collectionDate,
        vasectomyMoment: vasectomyMoment.format(),
        collectionMoment: collectionMoment.format(),
      })
      return '0.0'
    }

    // Calculate difference in days and convert to weeks
    const diffInDays = collectionMoment.diff(vasectomyMoment, 'days')
    const weeks = diffInDays / 7

    // Return as string with 1 decimal place
    return weeks.toFixed(1)
  }

  return (
    <>
      <Modal
        className="modal"
        open={open}
        onClose={(event, reason) => {
          if (reason === 'escapeKeyDown') {
            // Prevent closing on Escape
            return
          }
          clearCamera()
          clearStates()
          handleClose()
        }}
        slotProps={{
          backdrop: {
            onKeyDown: (event) => {
              if (event.key === 'Escape') {
                event.stopPropagation() // Block Escape key bubbling
              }
            },
          },
        }}
      >
        <Box className="test-modal-container">
          <h4>KitId {modalState?.kitId} - Record Test</h4>
          <hr style={{ margin: '8px 0' }} />
          {modalState && (
            <>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={activeTab} onChange={handleTabChange} aria-label="test record tabs">
                  <Tab
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Test Data Entry
                        {hasTab1Errors() && activeTab === 1 && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: '#d32f2f',
                              '@keyframes pulse': {
                                '0%': {
                                  opacity: 1,
                                },
                                '50%': {
                                  opacity: 0.5,
                                },
                                '100%': {
                                  opacity: 1,
                                },
                              },
                              animation: 'pulse 2s infinite',
                            }}
                          />
                        )}
                      </Box>
                    }
                    sx={{
                      color: hasTab1Errors() && activeTab === 1 ? '#d32f2f' : 'inherit',
                    }}
                  />
                  <Tab label="Image Capture" />
                </Tabs>
              </Box>

              {/* Tab Panel 1: Data Entry */}
              {activeTab === 0 && (
                <Box sx={{ maxHeight: '500px', overflowY: 'auto', p: 2 }}>
                  <Grid container spacing={2}>
                    {/* Patient Information Section */}
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2">
                              <strong>Name:</strong> {modalState?.patientName}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2">
                              <strong>Date of Birth:</strong> {formatDateToBrowserLocale(modalState?.patientDoB)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2">
                              <strong>Date of Vasectomy:</strong> {formatDateToBrowserLocale(modalState?.patientDoV)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>

                    {/* Collection Date and Calculation */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                        <FormControl>
                          <FormLabel sx={{ mb: 1 }}>Collection Date</FormLabel>
                          <TextField
                            name="collectionDate"
                            onChange={(event) => handleInput(event.target)}
                            value={resultRecord.collectionDate}
                            type="date"
                            variant="outlined"
                            helperText={errorState.collectionDate}
                            error={errorState.collectionDate ? true : false}
                          />
                        </FormControl>
                        <Box sx={{ p: 1.5, backgroundColor: '#e3f2fd', borderRadius: 1, minWidth: 180 }}>
                          <Typography variant="body2" className="weeksSinceVas">
                            <strong>
                              {resultRecord.collectionDate !== ''
                                ? calculateWeeksSinceVasectomy(modalState?.patientDoV, resultRecord.collectionDate)
                                : '0.0'}
                            </strong>{' '}
                            weeks since vasectomy
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Test Result */}
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <FormLabel sx={{ mb: 1 }}>Test Result</FormLabel>
                        <RadioGroup
                          name="result"
                          onChange={(event) => handleInput(event.target)}
                          className="radio-group"
                          value={resultRecord.result}
                        >
                          <FormControlLabel
                            value="NSS"
                            control={<Radio />}
                            label={<span className="btn-sm bg-success">NSS</span>}
                          />
                          <FormControlLabel
                            value="Positive"
                            control={<Radio />}
                            label={<span className="btn-sm bg-danger">Positive</span>}
                          />
                          <FormControlLabel
                            value="error"
                            control={<Radio />}
                            label={<span className="btn-sm bg-warning">Error</span>}
                          />
                        </RadioGroup>
                        <FormHelperText error={errorState.result ? true : false}>{errorState.result}</FormHelperText>
                      </FormControl>
                    </Grid>

                    {/* Sample Grade and Advice Code */}
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <FormLabel sx={{ mb: 1 }}>Test Sample Grade</FormLabel>
                        <Select
                          name="sampleGrade"
                          onChange={(event) => handleInput(event.target)}
                          value={resultRecord.sampleGrade}
                          variant="outlined"
                        >
                          <MenuItem value="NONE">NONE (0/hpf)</MenuItem>
                          <MenuItem value="RARE">RARE (&nbsp;1/hpf)</MenuItem>
                          <MenuItem value="MODERATE">MODERATE (~1-10/hpf)</MenuItem>
                          <MenuItem value="HIGH">HIGH (10+/hpf)</MenuItem>
                        </Select>
                        <FormHelperText error={errorState.sampleGrade ? true : false}>{errorState.sampleGrade}</FormHelperText>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <FormLabel sx={{ mb: 1 }}>Advice Code</FormLabel>
                        <Select
                          name="adviceCode"
                          onChange={(event) => handleInput(event.target)}
                          value={resultRecord.adviceCode}
                          disabled={adviceCodeDisabled}
                          variant="outlined"
                        >
                          {labTestAdviceCodeDetail.map((item) => {
                            return (
                              <MenuItem key={item.value} value={item.value}>
                                {item.value}
                              </MenuItem>
                            )
                          })}
                        </Select>
                        <FormHelperText sx={{ margin: '0px' }}>
                          {resultRecord.adviceCode && 'Calculated Advice Code'}
                        </FormHelperText>
                        <FormHelperText error={errorState.adviceCode ? true : false}>{errorState.adviceCode}</FormHelperText>
                      </FormControl>
                    </Grid>

                    {resultRecord.adviceCode !== '' && (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            backgroundColor: '#f0f7ff',
                            padding: 2,
                            borderRadius: 1,
                            border: '1px solid #e3f2fd',
                          }}
                        >
                          {(() => {
                            const details = labTestAdviceCodeDetail.find((item) => item.value === resultRecord.adviceCode)
                            return (
                              <Typography variant="body2">
                                <strong>{details.value}</strong> - <em>{details.use}</em>
                                <br />
                                {details.description}
                              </Typography>
                            )
                          })()}
                        </Box>
                      </Grid>
                    )}

                    {/* Notes Section */}
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <FormLabel sx={{ mb: 1 }}>Notes (MFH Only)</FormLabel>
                        <TextField
                          multiline
                          rows={4}
                          name="note"
                          onChange={(event) => handleInput(event.target)}
                          value={resultRecord.note}
                          variant="outlined"
                          error={errorState.note ? true : false}
                          helperText={errorState.note}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <FormLabel sx={{ mb: 1 }}>Provider Notes</FormLabel>
                        <TextField
                          multiline
                          rows={4}
                          name="providerNotes"
                          onChange={(event) => handleInput(event.target)}
                          value={resultRecord.providerNotes}
                          variant="outlined"
                          error={errorState.providerNotes ? true : false}
                          helperText={errorState.providerNotes}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Tab Panel 2: Image Capture */}
              {activeTab === 1 && (
                <Box sx={{ maxHeight: '500px', overflowY: 'auto', p: 2 }}>
                  <Grid container>
                    {/* Camera Preview Section - 70% width */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
                        {/* Video Preview - 80% */}
                        <Box sx={{ width: '80%' }}>
                          <Box
                            className="video-container"
                            sx={{
                              border: '2px solid #e0e0e0',
                              borderRadius: 2,
                              p: 2,
                              backgroundColor: '#fafafa',
                              display: 'flex',
                              justifyContent: 'center',
                            }}
                          >
                            <video
                              id="camera-video"
                              autoPlay
                              muted
                              playsInline
                              style={{
                                width: '100%',
                                maxWidth: '100%',
                                height: 'auto',
                                minHeight: '300px',
                                maxHeight: '500px',
                                borderRadius: '8px',
                                backgroundColor: '#000',
                              }}
                            ></video>
                          </Box>
                        </Box>
                        {/* Camera Controls - 20% */}
                        <Box
                          sx={{
                            width: '20%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <FormControl sx={{ width: '100%', mb: 2 }}>
                            <InputLabel>Select Camera</InputLabel>
                            <Select
                              value={selectedCamera}
                              label="Select Camera"
                              onChange={(event) => handleCameraSelect(event.target.value)}
                              disabled={!cameraPermissions.video}
                            >
                              {camerasList.map((camera, index) => {
                                return (
                                  <MenuItem key={index} value={camera.deviceId} selected={index === 0}>
                                    {camera.label || `Camera ${camerasList.indexOf(camera) + 1}`}
                                  </MenuItem>
                                )
                              })}
                            </Select>
                          </FormControl>

                          <Button
                            size="large"
                            onClick={async () => {
                              if (!cameraPermissions.video) {
                                notify('Camera permission is required. Please grant permission and reload the page.', {
                                  type: 'error',
                                })
                                return
                              }

                              const video = document.getElementById('camera-video')
                              if (!video || !video.srcObject) {
                                notify('Camera is not active. Please select a camera first.', { type: 'error' })
                                return
                              }

                              const totalImages = existingImages.length + capturedImages.length
                              if (totalImages >= 20) {
                                notify('Maximum of 20 images allowed. Please delete some images before capturing more.', {
                                  type: 'error',
                                })
                                return
                              }

                              const canvas = document.getElementById('img-canvas')
                              const ctx = canvas.getContext('2d')
                              canvas.width = 720
                              canvas.height = 480
                              ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

                              // White transparent box on which we render the watermark text
                              ctx.fillStyle = 'rgba(255, 255, 255, 0.5)'
                              ctx.fillRect(0, canvas.height - 30, 400, 30)

                              const tempList = [...capturedImages]
                              const imageDataUrl = canvas.toDataURL('image/png')
                              const imgWithWatermark = await watermark([imageDataUrl]).image(
                                watermark.text.lowerLeft(
                                  `MFH Lab Services  KitID ${modalState?.kitId}  ${moment().format('MM/DD/YYYY')}`,
                                  '14px courier new',
                                  '#000',
                                  0.8
                                )
                              )

                              tempList.push(imgWithWatermark.src)
                              setCapturedImages(tempList)
                            }}
                            variant="contained"
                            disabled={!selectedCamera || existingImages.length + capturedImages.length >= 20}
                            sx={{ px: 4, width: '100%' }}
                          >
                            Capture Image
                          </Button>

                          <Typography variant="body2" color="error">
                            {cameraPermissions.video
                              ? ''
                              : 'Camera permission is required. Please grant permission and reload the page.'}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Captured Images Section */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                        {`Captured Images (${existingImages.length + capturedImages.length}/20)`}
                        {existingImages.length + capturedImages.length >= 18 && (
                          <Typography variant="caption" color="warning.main" sx={{ ml: 1 }}>
                            - Approaching limit
                          </Typography>
                        )}
                      </Typography>
                      <Box className="img-capture-section">
                        <Box className="image-container" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                          {existingImages
                            .map((i) => i.url)
                            .concat(capturedImages)
                            .map((item, index) => {
                              return (
                                <Box
                                  className="img-thumb-container"
                                  key={index}
                                  sx={{
                                    border: '1px solid #ddd',
                                    borderRadius: 1,
                                    p: 1,
                                    backgroundColor: 'white',
                                  }}
                                >
                                  <Box
                                    className="img-thumb-actions"
                                    sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <input
                                        type="checkbox"
                                        id={`test-img-${index}`}
                                        name="patientIdCard"
                                        value={item}
                                        checked={patientIdCard.includes(item)}
                                        onChange={(e) =>
                                          setPatientIdCard(
                                            e.target.checked
                                              ? [...patientIdCard, e.target.value]
                                              : patientIdCard.filter((i) => i !== e.target.value)
                                          )
                                        }
                                      />
                                      <label htmlFor={`test-img-${index}`} style={{ fontSize: '12px' }}>
                                        Patient ID Card
                                      </label>
                                    </Box>
                                    <span
                                      onClick={async () => await handleDelete(item)}
                                      className="fa-solid fa-trash"
                                      style={{ cursor: 'pointer', color: '#d32f2f', fontSize: '14px' }}
                                      title="Delete image"
                                    ></span>
                                  </Box>
                                  <img
                                    className={'img-thumb' + (patientIdCard.includes(item) ? '-patientidcard' : '')}
                                    key={index}
                                    src={item}
                                    onClick={() => {
                                      setImagePreviewImagePath(item)
                                      handleImagePreview(true)
                                    }}
                                    alt="result"
                                    style={{
                                      width: '200px',
                                      height: '125px',
                                      objectFit: 'cover',
                                      cursor: 'pointer',
                                      borderRadius: '4px',
                                    }}
                                  />
                                </Box>
                              )
                            })}
                          {existingImages.length + capturedImages.length === 0 && (
                            <Box
                              sx={{
                                textAlign: 'center',
                                py: 4,
                                px: 2,
                                color: '#666',
                                border: '2px dashed #ccc',
                                borderRadius: 1,
                                width: '100%',
                              }}
                            >
                              <i
                                className="fa-solid fa-camera"
                                style={{ fontSize: '24px', marginBottom: '8px', display: 'block' }}
                              ></i>
                              <Typography variant="body2">No images captured yet</Typography>
                              <Typography variant="caption">Use the capture button to take photos</Typography>
                            </Box>
                          )}
                          <canvas style={{ display: 'none' }} id="img-canvas" width={200} height={200}></canvas>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Action Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                  mt: 3,
                  pt: 2,
                  borderTop: 1,
                  borderColor: 'divider',
                }}
              >
                <Box>
                  {activeTab === 1 && (
                    <Button
                      size="medium"
                      onClick={() => setActiveTab(0)}
                      variant="outlined"
                      startIcon={<i className="fa-solid fa-arrow-left"></i>}
                    >
                      Back to Data Entry
                    </Button>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  {activeTab === 0 ? (
                    <>
                      <Button
                        size="medium"
                        onClick={() => setActiveTab(1)}
                        variant="contained"
                        endIcon={<i className="fa-solid fa-arrow-right"></i>}
                      >
                        Next: Image Capture
                      </Button>
                      <Button
                        size="medium"
                        onClick={() => {
                          clearCamera()
                          clearStates()
                          handleClose()
                        }}
                        variant="outlined"
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="medium"
                        type="submit"
                        onClick={async (event) => {
                          event.preventDefault()
                          if (hasTab1Errors()) {
                            setActiveTab(0)
                            return
                          }
                          await handleSave()
                        }}
                        disabled={isSaving || hasTab1Errors()}
                        variant="contained"
                        startIcon={
                          hasTab1Errors() ? (
                            <i className="fa-solid fa-exclamation-triangle"></i>
                          ) : (
                            <i className="fa-solid fa-save"></i>
                          )
                        }
                        color={hasTab1Errors() ? 'error' : 'primary'}
                      >
                        {isSaving ? 'Saving...' : 'Save Test Record'}
                      </Button>
                      <Button
                        size="medium"
                        onClick={() => {
                          clearCamera()
                          clearStates()
                          handleClose()
                        }}
                        variant="outlined"
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            </>
          )}

          <ImagePreview imagePath={imagePreviewImagePath} open={imagePreviewState} setOpen={handleImagePreview} />
        </Box>
      </Modal>

      {/* Image Warning Dialog */}
      <Dialog
        open={showImageWarning}
        onClose={() => setShowImageWarning(false)}
        aria-labelledby="image-warning-dialog-title"
        aria-describedby="image-warning-dialog-description"
      >
        <DialogTitle id="image-warning-dialog-title">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <i className="fa-solid fa-exclamation-triangle" style={{ color: '#ff9800' }}></i>
            Insufficient Images
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="image-warning-dialog-description">
            You currently have <strong>{existingImages.length + capturedImages.length}</strong> image(s) captured.
            <br />
            <br />
            It is recommended to capture at least <strong>4 images</strong> for proper test documentation.
            <br />
            <br />
            Would you like to continue saving without the recommended number of images, or go back to capture more?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowImageWarning(false)}
            variant="outlined"
            startIcon={<i className="fa-solid fa-arrow-left"></i>}
          >
            Go Back & Capture More
          </Button>
          <Button onClick={handleConfirmSave} variant="contained" color="warning" startIcon={<i className="fa-solid fa-save"></i>}>
            Save Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default TestRecordModal
